import {
  COMMON_SUCCESS_TRANSLATION,
  GENERIC_ERR_MSG,
  GENERIC_EVENT_ERR_HEADING,
  GENERIC_EVENT_SUCCESS_HEADING,
} from "@Constants/snackbar";
import { COOKIE_KEYS } from "@Constants/storage";
import { withAsync } from "@Libs/api/withAsync";
import { loginEvent, signupEvent } from "@Libs/dynamic-yield/trackingEvents";
import {
  IRegisterFormPayload,
  TAccountResetPayload,
  TForgotPasswordPayload,
} from "@Types/Account";
import { SnackBarType, SnackBarVariant } from "@Types/SnackBar";
import { TDispatch } from "@Types/common";
import logger from "@Utils/Logger";
import { getCookie } from "@Utils/cookies";
import { getReduxStore } from "@Utils/reduxCommon";
import {
  axiosErrorHandler,
  getScenariosFromErrorCodeTranslation,
  IScenario,
} from "@Utils/snackbarHandlers";
import { ERROR_CODES } from "@wff/api/constants/error";
import {
  submitAccountResetForm,
  submitForgotPasswordForm,
  submitLoginForm,
  submitSignupForm,
} from "@wff/api/customerApi";
import {
  ILogin,
  ISignup,
  ISubmitFormActionForgotPassword,
  ISubmitFormActionLogin,
  ISubmitFormActionSignup,
  ISubmitResetAccountPasswordFormAction,
} from "@wff/interfaces";
import { triggerSnackBar } from "@wff/store/SnackBarSlice";
import { resetCart } from "@wff/store/cartSlice";

export const resetReduxCartIfCartIdRemovedFromCookies = (
  dispatch: TDispatch
) => {
  const {
    cart: { cartId },
  } = getReduxStore();
  const cookieCartId = getCookie(COOKIE_KEYS.CART_ID);

  if (!cookieCartId && cartId) {
    dispatch(resetCart());
  }
};

export const submitFormActionSignup = async ({
  dispatch,
  payload,
  setFormError,
  errorCodeTranslation,
  actionOnFormSuccess,
}: ISubmitFormActionSignup) => {
  const newPayload: IRegisterFormPayload = { ...payload };
  // NOTE: Make sure that we are Passing ONLY the values that are needed
  delete (newPayload as Partial<ISignup>).confirmedPassword;
  const { error } = await withAsync(() => {
    return submitSignupForm(newPayload);
  });

  if (error) {
    setFormError(error);
    let scenarios: IScenario[] = [];
    if (errorCodeTranslation) {
      scenarios = getScenariosFromErrorCodeTranslation({
        errorCodeTranslation,
      });
    }
    axiosErrorHandler({
      error,
      dispatch,
      scenarios,
      fallbackSnackbar: {
        heading: GENERIC_EVENT_ERR_HEADING.REQUEST_FAILED(),
        message: GENERIC_ERR_MSG.ERROR(),
      },
      errorCodeIgnoreList: [ERROR_CODES.WF_CUSTOMER_001],
    });
  } else {
    signupEvent(payload.email); // Track `Signup` Event for DY
    actionOnFormSuccess();
    resetReduxCartIfCartIdRemovedFromCookies(dispatch);
  }
};

export const submitFormActionLogin = async ({
  dispatch,
  payload,
  setFormError,
  errorCodeTranslation,
  actionOnFormSuccess,
}: ISubmitFormActionLogin) => {
  const newPayload: ILogin = { ...payload };
  newPayload.email = newPayload.email.trim(); // for trimming extra space in email field
  // NOTE: Make sure that we are Passing ONLY the values that are needed
  const { error } = await withAsync(() => submitLoginForm(newPayload));
  if (error) {
    setFormError(error);
    let scenarios: IScenario[] = [];
    if (errorCodeTranslation) {
      scenarios = getScenariosFromErrorCodeTranslation({
        errorCodeTranslation,
      });
    }
    axiosErrorHandler({
      error,
      dispatch,
      scenarios,
      fallbackSnackbar: {
        heading: GENERIC_EVENT_ERR_HEADING.REQUEST_FAILED(),
        message: GENERIC_ERR_MSG.ERROR(),
      },
      errorCodeIgnoreList: [ERROR_CODES.WF_CUSTOMER_004],
    });
  } else {
    loginEvent(payload.email); // Track `Login` Event for DY
    actionOnFormSuccess();
    resetReduxCartIfCartIdRemovedFromCookies(dispatch);
  }
};

export const submitFormActionForgotPassword = async ({
  dispatch,
  payload,
  setFormError,
  errorCodeTranslation,
  languageCode,
}: ISubmitFormActionForgotPassword) => {
  const signupPayload: TForgotPasswordPayload = { ...payload };
  // NOTE: Make sure that we are Passing ONLY the values that are needed
  const { error } = await withAsync(() => {
    return submitForgotPasswordForm(signupPayload);
  });
  if (error) {
    setFormError(error);
    let scenarios: IScenario[] = [];
    if (errorCodeTranslation) {
      scenarios = getScenariosFromErrorCodeTranslation({
        errorCodeTranslation,
      });
    }
    axiosErrorHandler({
      error,
      dispatch,
      scenarios,
      fallbackSnackbar: {
        heading: GENERIC_EVENT_ERR_HEADING.REQUEST_FAILED(),
        message: GENERIC_ERR_MSG.ERROR(),
      },
    });
  } else {
    dispatch(
      triggerSnackBar({
        title: GENERIC_EVENT_SUCCESS_HEADING.SUCCESS(languageCode),
        message: COMMON_SUCCESS_TRANSLATION.CHECK_YOUR_EMAIL(languageCode),
        type: "success" as SnackBarType,
        variant: "filled" as SnackBarVariant,
      })
    );
  }
};

export const submitFormActionResetAccountPassword = async ({
  dispatch,
  payload,
  setFormError,
  errorCodeTranslation,
  actionOnFormSuccess,
}: ISubmitResetAccountPasswordFormAction) => {
  if (!payload.token) {
    logger.error(
      "There was no Password Token ID Provided to include in the password reset payload"
    );
  }
  const newPayload: TAccountResetPayload = { ...payload };
  // NOTE: Make sure that we are Passing ONLY the formState values that are needed
  delete (newPayload as Partial<TAccountResetPayload>).confirmedPassword;
  const { error } = await withAsync(() => {
    return submitAccountResetForm(newPayload);
  });
  if (error) {
    setFormError(error);
    let scenarios: IScenario[] = [];
    if (errorCodeTranslation) {
      scenarios = getScenariosFromErrorCodeTranslation({
        errorCodeTranslation,
      });
    }
    axiosErrorHandler({
      error,
      dispatch,
      scenarios,
      fallbackSnackbar: {
        heading: GENERIC_EVENT_ERR_HEADING.REQUEST_FAILED(),
        message: GENERIC_ERR_MSG.ERROR(),
      },
    });
  } else {
    actionOnFormSuccess();
  }
};
